import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import type { CarryField, MemoryCard } from '../api/useHypoteseHukommelse.ts';
import { useHypoteser, type Hypothesis, type HypothesisConfig, type HypothesisSummary } from '../api/useHypotheses.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { resultatOrd } from './kilder.ts';
import { nummerTekst } from './Kvittering.tsx';
import { fornavn, navnAv, oppramsing, statusNavn, stor } from './lesning.ts';
import { kortDato, kortDatoAv } from './setning.ts';
import type { Ventende } from './useAngre.ts';

/**
 * Vurdererens side av hukommelsen (H3.3, konseptsiden W11–W13):
 *
 * - «Bygger på en som er vurdert før»: den tidligere hypotesen som kort, og innsenderens egen
 *   setning om hva som er annerledes, ordrett og med navn.
 * - «Ligner på»: inntil tre åpne hypoteser som ligner — forslag, aldri en sammenslåing av seg selv.
 * - «Slå sammen» (overgang 13): en dialog (DD-47) der vurdereren velger hvem som fører videre,
 *   henter over ledd som mangler, og ser svaret begge får. Kallet holdes i fem sekunder med
 *   «Angre» av siden (`useAngre`); det finnes ikke noe angre-endepunkt.
 */

/** W11: «Bygger på en som er vurdert før» — `supersedesCard` og `differenceNote`. */
export function ByggerPaa({ h, config, tittelTag: T }: { h: Hypothesis; config: HypothesisConfig | undefined; tittelTag: 'h2' | 'h3' }) {
  const k: MemoryCard | undefined = h.supersedesCard;
  if (!k) return null;
  const nr = nummerTekst(k.hypothesis.number);
  const o = hypoteseOppforing(hypoteseStatusNoekkel(k.status, k.outcome));
  const linje = [nr, k.hypothesis.title, k.result ? resultatOrd(k.result, ', ') : null, kortDato(k.on)].filter(Boolean).join(' · ');
  return (
    <section className="hb-kort hs-bygger" aria-labelledby="hs-bygger-h" data-testid="hb-bygger">
      <div className="hb-kort-hode">
        <T id="hs-bygger-h" className="hs-kort-tittel">
          <Ikon navn="list-outline" /> Bygger på en som er vurdert før
        </T>
        <Link to={`/h/registeret/${k.hypothesis.number}`} className="panel-knapp-lenke">
          Åpne {nr}
        </Link>
      </div>
      <p className="hs-bygger-linje">
        <span className="h-lp-status" style={{ '--hb-status': tokenVar(o.fg) } as CSSProperties}>
          <Ikon navn={o.ikon} />
          {statusNavn(config, k)}
        </span>
        <span>{linje}</span>
      </p>
      {h.differenceNote ? (
        <p className="hs-annerledes">
          <Ikon navn="person-outline" />
          <span>
            <b>{stor(fornavn(h.submittedBy))} sier hva som er annerledes:</b> {h.differenceNote}
          </span>
        </p>
      ) : null}
    </section>
  );
}

/** W11/W12: «Ligner på» i detaljen — veien til «Slå sammen». */
export function LignerPaaKandidater({ kandidater, tittelTag: T, onSlaaSammen }: { kandidater: HypothesisSummary[]; tittelTag: 'h2' | 'h3'; onSlaaSammen: (k: HypothesisSummary) => void }) {
  if (kandidater.length === 0) return null;
  return (
    <section className="hs-ligner" aria-labelledby="hs-ligner-h" data-testid="hb-ligner">
      <T id="hs-ligner-h" className="hs-kort-tittel">
        <Ikon navn="git-merge-outline" /> Ligner på
      </T>
      <ul className="hs-ligner-liste">
        {kandidater.map((k, i) => (
          <li key={k.id} className="hs-ligner-rad">
            <span className="hs-ligner-tekst">
              <b>{k.title}</b>
              <span className="hb-liten">
                {nummerTekst(k.number)} · fra {navnAv(k.submittedBy)}
                {k.submittedAt ? ` · ${kortDatoAv(k.submittedAt)}` : ''}
              </span>
            </span>
            <button type="button" className="panel-knapp-sekundaer" aria-keyshortcuts={i === 0 ? 'm' : undefined} onClick={() => onSlaaSammen(k)} data-testid={`hb-slaa-sammen-${k.number}`}>
              Slå sammen med {nummerTekst(k.number)} …{i === 0 ? <kbd aria-hidden="true">M</kbd> : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const LEDDNAVN: Record<CarryField, { navn: string; felt: 'whatWeLose' | 'background' }> = {
  what_we_lose: { navn: 'Mister vi noe om vi venter?', felt: 'whatWeLose' },
  background: { navn: 'Hvorfor tror du det?', felt: 'background' },
};

/** Alle som får svaret: innsenderen og medinnsenderne, uten dobbelt. */
const mottakere = (x: Hypothesis | HypothesisSummary): string[] => [x.submittedBy, ...(x.coSubmitters ?? []).map((c) => c.admin)].map(navnAv);

/**
 * W12: «Slå sammen H-149 med H-143». Forhåndsvalgt på den eldste som fører videre. «Hent over»
 * flytter tekst inn i et ledd som MANGLER i den som fører videre — aldri flettet inn i det som står.
 */
export function SlaaSammenDialog({ h, kandidat, config, onLukk, onHold }: { h: Hypothesis; kandidat: HypothesisSummary; config: HypothesisConfig | undefined; onLukk: () => void; onHold: (v: Ventende) => void }) {
  const detaljer = useHypoteser([h.id, kandidat.id]);
  const andre = detaljer[1]?.data;
  const eldst = (kandidat.submittedAt ?? kandidat.createdAt) < (h.submittedAt ?? h.createdAt) ? kandidat.id : h.id;
  const [lederId, setLederId] = useState(eldst);
  const [hent, setHent] = useState<CarryField[]>([]);
  const mal = useMemo(() => config?.responseTemplates.find((m) => m.use === 'slaatt_sammen'), [config]);

  const leder: Hypothesis | HypothesisSummary = lederId === h.id ? h : (andre ?? kandidat);
  const slaatt: Hypothesis | HypothesisSummary = lederId === h.id ? (andre ?? kandidat) : h;
  const malTekst = (l: Hypothesis | HypothesisSummary) => {
    const fra = navnAv(l.submittedBy);
    const nr = nummerTekst(l.number);
    return mal ? mal.text.replaceAll('{navn}', fra).replaceAll('{nummer}', nr) : `Hypotesen din er slått sammen med en lignende fra ${fra}. Du står som medinnsender på ${nr} og følger den videre der.`;
  };
  const [svar, setSvar] = useState(() => malTekst(leder));
  const [fylt, setFylt] = useState(() => malTekst(leder));

  const velgLeder = (id: string) => {
    setLederId(id);
    setHent([]);
    const ny = malTekst(id === h.id ? h : (andre ?? kandidat));
    // Teksten følger lederen så lenge vurdereren ikke har skrevet i den.
    if (svar === fylt) setSvar(ny);
    setFylt(ny);
  };

  const mangler = (Object.keys(LEDDNAVN) as CarryField[]).filter((c) => {
    const f = LEDDNAVN[c].felt;
    const fraTekst = (slaatt as Hypothesis)[f];
    const iLeder = (leder as Hypothesis)[f];
    return Boolean(fraTekst && fraTekst.trim()) && !(iLeder && iLeder.trim());
  });
  const lastet = detaljer.every((d) => d.data);
  const grunn = !svar.trim() ? 'Skriv svaret begge får.' : !lastet ? 'Henter hypotesene …' : null;
  const navn = oppramsing([...new Set([...mottakere(slaatt), ...mottakere(leder)])]);

  const slaaSammen = () => {
    if (grunn) return;
    const tekst = svar.trim();
    onHold({
      id: slaatt.id,
      nummer: slaatt.number,
      overgang: { handling: 'merge', body: { targetId: leder.id, carryOver: hent, response: { text: tekst, ...(mal ? { templateId: mal.id } : {}) } } },
      tekst: `${nummerTekst(slaatt.number)} er slått sammen med ${nummerTekst(leder.number)}. Svaret går til ${navn} om 5 sekunder.`,
    });
  };
  const paaTast = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      slaaSammen();
    }
  };

  const kort = (x: Hypothesis | HypothesisSummary) => (
    <label key={x.id} className={x.id === lederId ? 'hs-kandidat hs-kandidat-valgt' : 'hs-kandidat'}>
      <span className="hs-kandidat-hode">
        <input type="radio" name="hs-leder" value={x.id} checked={x.id === lederId} onChange={() => velgLeder(x.id)} />
        <span className="hb-fet">Fører videre</span>
      </span>
      <span className="hb-liten">
        {nummerTekst(x.number)} · {navnAv(x.submittedBy)}
        {x.submittedAt ? ` · ${kortDatoAv(x.submittedAt)}` : ''} · {statusNavn(config, x)}
      </span>
      <span className="hb-fet hs-kandidat-tittel">{x.title}</span>
      {x.sentence ? <span className="hb-dempet hs-kandidat-setning">{x.sentence}</span> : null}
    </label>
  );

  const fraNr = nummerTekst(slaatt.number);
  const tilNr = nummerTekst(leder.number);
  return (
    <Dialog tittelId="hs-dlg-h" onLukk={onLukk} className="hs-dialog" testid="hs-dialog">
      <div className="hs-dialog-innhold" onKeyDown={paaTast}>
        <h2 id="hs-dlg-h" className="panel-h2">
          Slå sammen {fraNr} med {tilNr}
        </h2>
        <p className="hb-dempet">Velg hvilken som fører videre. Den andre får status {statusNavn(config, { status: 'slaatt_sammen' }) || 'Slått sammen'}, og innsenderen står som medinnsender på den som fører videre.</p>
        <div role="radiogroup" aria-labelledby="hs-leder-h" className="hs-kandidater">
          <span id="hs-leder-h" className="panel-skjult">
            Fører videre
          </span>
          {/* Den eldste står først — den er forhåndsvalgt (W12). */}
          {eldst === h.id ? kort(h) : kort(andre ?? kandidat)}
          {eldst === h.id ? kort(andre ?? kandidat) : kort(h)}
        </div>
        <div role="group" aria-label={`Hent over fra ${fraNr} — ledd som mangler i ${tilNr}`} className="hs-hent">
          <p className="h-etikett">
            Hent over fra {fraNr} — ledd som mangler i {tilNr}
          </p>
          {!lastet ? (
            <p className="hb-dempet">Henter leddene …</p>
          ) : mangler.length === 0 ? (
            <p className="hb-dempet">Ingen ledd mangler i {tilNr}.</p>
          ) : (
            mangler.map((c) => {
              const paa = hent.includes(c);
              return (
                <div key={c} className="hs-hent-rad">
                  <span className="hs-hent-tekst">
                    <b>{LEDDNAVN[c].navn}</b>
                    <span>{(slaatt as Hypothesis)[LEDDNAVN[c].felt]}</span>
                  </span>
                  <button type="button" className={paa ? 'panel-knapp-lenke hs-hentet' : 'panel-knapp-lenke'} aria-pressed={paa} aria-label={`Hent over «${LEDDNAVN[c].navn}»`} onClick={() => setHent((x) => (paa ? x.filter((y) => y !== c) : [...x, c]))}>
                    {paa ? (
                      <>
                        <Ikon navn="checkmark-outline" />
                        <span>Hentet over</span>
                      </>
                    ) : (
                      'Hent over'
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div className="h-felt">
          <label htmlFor="hs-svar" className="h-etikett">
            Svaret begge får
          </label>
          <textarea id="hs-svar" className="h-input h-tekstfelt" rows={3} maxLength={500} value={svar} onChange={(e) => setSvar(e.target.value)} />
          <span className="hb-liten">Går til {navn}.</span>
        </div>
        <div className="panel-handlinger hs-knapper">
          <Knapp label="Avbryt" lenke onClick={onLukk} />
          <Knapp label="Slå sammen" kbd="Ctrl+Enter" primaer grunn={grunn} onClick={slaaSammen} testid="hs-slaa-sammen" />
        </div>
      </div>
    </Dialog>
  );
}

/** Pillen og linjen for en sammenslått hypotese i detaljen: «følges videre i H-143». */
export function FoelgesVidere({ h, config }: { h: HypothesisSummary; config: HypothesisConfig | undefined }) {
  if (!h.mergedInto) return null;
  return (
    <p className="hs-videre">
      <HypotesePille noekkel="slaatt_sammen" navn={statusNavn(config, h) || 'Slått sammen'} />
      <span>
        følges videre i <Link to={`/h/registeret/${h.mergedInto.number}`}>{nummerTekst(h.mergedInto.number)}</Link>
      </span>
    </p>
  );
}
